import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { visuallyHidden } from '@mui/utils';
import { useEffect } from 'react';
import { getTariffByTerminalID } from '../../../services/Tariff';

const headCells = [
  {
    label: 'ID',
    id: 'ID',
    disablePadding: false,
  },
  {
    label: 'Domain Name',
    id: 'Name',
    disablePadding: false,
  },
  {
    label: 'Tariff Name',
    id: 'Tariff',
    disablePadding: false,
  },
  {
    label: 'Tariff Value',
    id: 'Value',
    disablePadding: false,
  },
  {
    label: 'Currency',
    id: 'Currency',
    disablePadding: false,
  },
  {
    label: 'Start',
    id: 'Start',
    disablePadding: false,
  },
  {
    label: 'End',
    id: 'transdate',
    disablePadding: false,
  },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}
function BasicTable(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ fontWeight: '550' }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

BasicTable.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

export const MainTariff = ({ record }) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    getTariffByTerminalID(record.id).then((result) => {
      setRows(result);
      setCount(result.length);
    });
    // eslint-disable-next-line
  }, [page, rowsPerPage, orderBy, order]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - count) : 0;
  return (
    <Box sx={{ p: 2, pt: 0 }}>
      <Paper sx={{ width: '100%', mt: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table aria-labelledby="tableTitle" size="medium">
            <BasicTable
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={count}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy))?.map(
                (row, index) => {
                  return (
                    <TableRow key={index} hover>
                      <TableCell sx={{ minWidth: '6em' }}>{row.id}</TableCell>
                      <TableCell sx={{ minWidth: '12em' }}>
                        {row.domainname}
                      </TableCell>
                      <TableCell sx={{ minWidth: '12em' }}>
                        {row.tariffname}
                      </TableCell>
                      <TableCell sx={{ minWidth: '12em' }}>
                        {row.tariffvalue}
                      </TableCell>
                      <TableCell sx={{ minWidth: '8em' }}>
                        {row.currency}
                      </TableCell>
                      <TableCell sx={{ minWidth: '8em' }}>
                        {row.tariffstartdate}
                      </TableCell>
                      <TableCell sx={{ minWidth: '8em' }}>
                        {row.datefrom}
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={7} />
              </TableRow>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};
